// Data access — all calls go to the Go backend at /api/*.
// Firebase is used only for auth (getIdToken).

import { auth } from "./init.js";
import { API_BASE } from "./config.js";

// ── helpers ───────────────────────────────────────────────────────────────────

async function idToken() {
  if (!auth?.currentUser) return null;
  return auth.currentUser.getIdToken();
}

async function apiFetch(path, { method = "GET", body, auth: requireAuth = false } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (requireAuth) {
    const token = await idToken();
    if (!token && requireAuth !== "optional") throw new Error("Not signed in");
    if (token) headers["Authorization"] = "Bearer " + token;
  }
  const res = await fetch(API_BASE + path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`API ${method} ${path} → ${res.status}: ${text}`);
  }
  return res.json();
}

// ── Slug / claim flow ─────────────────────────────────────────────────────────

export async function checkSlugAvailable(slug) {
  const data = await apiFetch(`/slugs/check?slug=${encodeURIComponent(slug)}`);
  return data; // { available: boolean, reason?: string }
}

export async function reserveSlug(slug, eventType) {
  return apiFetch("/slugs/reserve", { method: "POST", body: { slug, eventType }, auth: "optional" });
  // returns { slug, claimToken }
}

export async function finalizeClaim(slug, claimToken) {
  return apiFetch("/slugs/finalize", {
    method: "POST",
    body: { slug, claimToken },
    auth: true,
  });
  // returns { ok: true }
}

// ── Events ────────────────────────────────────────────────────────────────────

export async function getEvent(slug, search = "") {
  try {
    const suffix = search ? (search.startsWith("?") ? search : `?${search}`) : "";
    return await apiFetch(`/events/${encodeURIComponent(slug)}${suffix}`);
  } catch (e) {
    if (e.message.includes("404")) return null;
    throw e;
  }
}

export async function saveEventDetails(slug, patch) {
  // patch may contain { details, theme } — send only what's provided
  await apiFetch(`/events/${encodeURIComponent(slug)}`, {
    method: "PUT",
    body: patch,
    auth: true,
  });
}

export async function publishEvent(slug, og) {
  await apiFetch(`/events/${encodeURIComponent(slug)}/publish`, {
    method: "POST",
    body: { og: og ?? {} },
    auth: true,
  });
}

export async function listMyEvents() {
  return apiFetch("/events", { auth: true });
}

export async function deleteEvent(slug) {
  return apiFetch(`/events/${encodeURIComponent(slug)}`, {
    method: "DELETE",
    auth: true,
  });
}

export async function subscribeToEvent(slug, subscriber) {
  return apiFetch(`/events/${encodeURIComponent(slug)}/subscriptions`, {
    method: "POST",
    body: subscriber,
  });
}

export async function listEventSubscriptions(slug) {
  const data = await apiFetch(`/events/${encodeURIComponent(slug)}/subscriptions`, { auth: true });
  return data ?? [];
}

export async function submitEventRegistration(slug, values) {
  return apiFetch(`/events/${encodeURIComponent(slug)}/registrations`, {
    method: "POST",
    body: { values },
  });
}

export async function listEventRegistrations(slug) {
  const data = await apiFetch(`/events/${encodeURIComponent(slug)}/registrations`, { auth: true });
  return data ?? [];
}
